import i18n from "@/language/index";
import { statusLabel } from "@/utils/bookingStatus";

/**
 * i18n table for the `code` field of a 403 body. The 4.3.x backend answers 26
 * of its 32 denials with the generic `forbidden`; the five specific codes
 * belong to strands that land later and add their entry here. An unknown code
 * falls back to the generic entry rather than inventing a message.
 */
const FORBIDDEN_CODE_PREFIX = "errors.forbidden-codes";
const GENERIC_FORBIDDEN_KEY = `${FORBIDDEN_CODE_PREFIX}.forbidden`;
const SESSION_EXPIRED_KEY = "errors.session-expired";

/**
 * i18n table for the `code` of a 409: the booking lifecycle refusing a
 * transition that no longer fits the stored state (spec E5). `invalid_transition`
 * names the state the booking is in now when `params.status` carries it, and
 * lists the diverging members when a group transition sends
 * `params.bookingIds`. An unknown code, an empty body and a body without a
 * code fall back to the generic entry.
 */
const CONFLICT_CODE_PREFIX = "errors.conflict-codes";
const GENERIC_CONFLICT_KEY = `${CONFLICT_CODE_PREFIX}.conflict`;
const INVALID_TRANSITION_CODE = "invalid_transition";
const INVALID_TRANSITION_UNKNOWN_STATUS_KEY = `${CONFLICT_CODE_PREFIX}.invalid_transition-unknown-status`;
const DIVERGING_BOOKINGS_KEY = `${CONFLICT_CODE_PREFIX}.diverging-bookings`;

/**
 * The lifecycle's codes on the two other statuses. A 404 `booking_not_found`
 * says the booking is gone; every other 404 keeps the caller's fallback,
 * because "not found" reads differently per screen. A 400
 * `invalid_status_change` is unreachable once the admin PUT carries no flags
 * (spec E1), and named anyway; the create PUT's `invalid_status` and
 * `missing_payment_details` (spec E10) are read the same way.
 */
const NOT_FOUND_CODE_PREFIX = "errors.not-found-codes";
const BAD_REQUEST_CODE_PREFIX = "errors.bad-request-codes";

/**
 * Saving an access point makes the backend ask the provider (Nuki lists its
 * locks to check the mode). A provider that refuses the tenant's token
 * answers 502 `access_provider_rejected`, one that does not answer at all 503
 * `access_provider_unreachable`, both with `params.provider`. An unknown code
 * and a body without one read as the generic entry of the status - which a
 * bare gateway 502 or a maintenance 503 reaches on any screen, so it names
 * neither a provider nor a save.
 */
const BAD_GATEWAY_CODE_PREFIX = "errors.bad-gateway-codes";
const GENERIC_BAD_GATEWAY_KEY = `${BAD_GATEWAY_CODE_PREFIX}.bad_gateway`;
const UNAVAILABLE_CODE_PREFIX = "errors.unavailable-codes";
const GENERIC_UNAVAILABLE_KEY = `${UNAVAILABLE_CODE_PREFIX}.unavailable`;

/**
 * The status the Admin BFF answers a failed CSRF check with (`bff/src/csrf.js`).
 * It is not a backend `ForbiddenError`: the request never reached the backend,
 * and the user is not lacking a permission - the browser sent a mutating
 * request whose `Origin`/`Referer` no longer matches the served origin, which
 * a reload fixes. 419 is unassigned in the IANA registry, so it cannot
 * contradict a registered meaning the way 428 (Precondition Required) would.
 */
export const CSRF_FAILED_STATUS = 419;

/**
 * The `code` of a 4.3.x error body. It comes in two forms: `BaseError.toJSON`
 * sends `{ error, code, statusCode, params }`, the customer's request-reject
 * route sends `{ code, message }` without a `statusCode`. A body whose
 * `statusCode` contradicts the response status is not read - and a body
 * without any `code` (an empty body, a raw string, an older BFF's own CSRF
 * 403 of `{ success, message }`) reads as the generic entry of its status.
 */
function getErrorCode(data, status) {
  if (!data || typeof data !== "object") {
    return null;
  }
  if (typeof data.code !== "string" || !data.code) {
    return null;
  }
  if (data.statusCode !== undefined && data.statusCode !== status) {
    return null;
  }
  return data.code;
}

/**
 * `params` are empty on every 403 the backend sends today; a 409 carries the
 * booking's state and, for a group transition, the diverging members. They are
 * handed to the translation for interpolation either way.
 */
function getErrorParams(data) {
  const params = data?.params;
  return params && typeof params === "object" ? params : {};
}

function getForbiddenMessage(data) {
  const code = getErrorCode(data, 403);
  const key = code ? `${FORBIDDEN_CODE_PREFIX}.${code}` : null;
  const params = getErrorParams(data);

  if (key && i18n.te(key)) {
    return i18n.t(key, params);
  }
  return i18n.t(GENERIC_FORBIDDEN_KEY, params);
}

/** The table entry for `code` under `prefix`, or `null` when there is none. */
function getCodedMessage(data, status, prefix) {
  const code = getErrorCode(data, status);
  const key = code ? `${prefix}.${code}` : null;
  return key && i18n.te(key) ? i18n.t(key, getErrorParams(data)) : null;
}

function getConflictMessage(data) {
  // A readable string body is already the message, the way the 400 branch
  // reads one; the lifecycle's own 409s always carry a code.
  if (typeof data === "string" && data.trim()) {
    return data.trim();
  }
  const code = getErrorCode(data, 409);
  const params = getErrorParams(data);
  let message;

  if (code === INVALID_TRANSITION_CODE) {
    const status = statusLabel(params.status);
    message = status
      ? i18n.t(`${CONFLICT_CODE_PREFIX}.${code}`, { status })
      : i18n.t(INVALID_TRANSITION_UNKNOWN_STATUS_KEY);
  } else if (code && i18n.te(`${CONFLICT_CODE_PREFIX}.${code}`)) {
    message = i18n.t(`${CONFLICT_CODE_PREFIX}.${code}`, params);
  } else {
    message = i18n.t(GENERIC_CONFLICT_KEY);
  }

  return withDivergingBookings(message, params.bookingIds);
}

/**
 * A message with the members of a group that deviate from its state named
 * after it („Betroffene Buchungen: …“), as the 409 of a group transition and
 * the 200 `STATUS_MISMATCH` of its consistency check send them; the message
 * alone without any.
 */
export function withDivergingBookings(message, bookingIds) {
  if (!Array.isArray(bookingIds) || bookingIds.length === 0) {
    return message;
  }
  return `${message} ${i18n.t(DIVERGING_BOOKINGS_KEY, {
    ids: bookingIds.join(", "),
  })}`;
}

/**
 * True when a request was refused. It exists to keep two things apart that a
 * list screen must never confuse: a 200 with `[]` means "nothing there", a 403
 * means "no access". Callers use it to pick between an ordinary empty list and
 * a permission notice - never to turn a legitimately empty result into a
 * permission error.
 *
 * It reads the status only, which is precise now that the BFF answers a failed
 * CSRF check with `CSRF_FAILED_STATUS` instead of a 403 of its own. Against a
 * deployment still running an older BFF the old imprecision remains, and it
 * cannot bite these callers: the CSRF guard only fires on writes and every
 * call site here is a GET.
 */
export function isForbiddenError(error) {
  return error?.response?.status === 403;
}

/**
 * Whether a 403 names `code`, read as the messages read it: a body whose
 * `statusCode` contradicts the status names none.
 */
export function isForbiddenCode(error, code) {
  return (
    isForbiddenError(error) && getErrorCode(error.response.data, 403) === code
  );
}

/**
 * Whether an error says the record is outside the caller's reach. Since 4.3.x
 * a record the caller may not see answers 404 rather than 403, so that its
 * existence stays hidden - which turns the two statuses into one question
 * wherever the UI only wants to know "may this be shown at all?".
 *
 * Use it for that question only. A message that has to *name* a reason still
 * looks at the status itself, because "not yours" and "gone" read differently
 * to the person on the screen. It is the wider question than
 * `isForbiddenError` above, which the list screens ask to tell an empty result
 * from a denied one.
 */
export function isOutOfReach(error) {
  const status = error?.response?.status;
  return status === 403 || status === 404;
}

/**
 * Whether an access command was refused because the lock is still carrying
 * out its previous command. The backend answers a real HTTP 423 with
 * `code: "lock_busy"` (`LockBusyError`); either half alone is enough, so a
 * bare 423 and a body naming the code both read as busy. It is a state of the
 * lock, not a denial: the caller shows it and lets the person try again.
 */
export function isLockBusyError(error) {
  return (
    error?.response?.status === 423 ||
    error?.response?.data?.code === "lock_busy"
  );
}

/**
 * The refetch rule of spec E5: after every 409 and 404 the host reloads the
 * booking (form, detail) or the list (list, calendar, kanban), so that the
 * screen shows the server's state instead of the one the transition was
 * attempted against. A 409 says the state moved under the user, a 404 that
 * the booking is gone or out of reach - either way what is on screen is
 * stale. The message stays `getApiErrorMessage`'s business.
 *
 * It overlaps `isOutOfReach` on the 404 only and answers a different
 * question: not "may this be shown at all?" but "is what is shown still
 * current?" - a 403 leaves the screen as it was, a 409 does not.
 */
export function shouldRefetch(error) {
  const status = error?.response?.status;
  return status === 409 || status === 404;
}

/**
 * The text of one detail of a booking's 400 `ValidationError`
 * (`booking.validation.<field>.<code>.<part>`, `part` being `title` or
 * `message`), or `null` when the table has no entry for it. The booking form's
 * toasts and the transitions' reasons both read it here.
 */
export function bookingValidationText(detail, part) {
  const key = `booking.validation.${detail?.field}.${detail?.code}.${part}`;
  return i18n.te(key) ? i18n.t(key, detail.params) : null;
}

/**
 * The reasons of a booking's 400 `ValidationError` (`details[]` with field and
 * code): the stored booking does not pass the backend's schema, so a
 * transition could not write it. Each detail reads as the title of the booking
 * form's validation table, a field without one by its name. Any other error
 * has none.
 */
export function getBookingValidationReasons(error) {
  const data = error?.response?.data;
  if (
    error?.response?.status !== 400 ||
    data?.error !== "ValidationError" ||
    !Array.isArray(data.details)
  ) {
    return [];
  }
  return data.details.map(
    (detail) =>
      bookingValidationText(detail, "title") ??
      i18n.t("booking.validation-reason-unknown", { field: detail.field })
  );
}

/**
 * Extract a displayable message from an axios error response. A 400 with a
 * plain-text body (e.g. the server-side PDF template validation of
 * `PUT /api/tenants`) returns that text, a 403 the message translated over
 * `code`, a 409 the conflict translated over `code`, a 419 the hint that the
 * session is no longer fresh, a 502 and a 503 the provider failure translated
 * over `code`; anything else returns the fallback.
 */
export function getApiErrorMessage(error, fallback) {
  if (error?.response?.status === 400) {
    const data = error.response.data;
    const coded = getCodedMessage(data, 400, BAD_REQUEST_CODE_PREFIX);
    if (coded) {
      return coded;
    }
    if (typeof data === "string" && data.trim()) {
      return data.trim();
    }
    if (data && typeof data.message === "string" && data.message.trim()) {
      return data.message.trim();
    }
  }
  if (error?.response?.status === 403) {
    return getForbiddenMessage(error.response.data);
  }
  if (error?.response?.status === 404) {
    return (
      getCodedMessage(error.response.data, 404, NOT_FOUND_CODE_PREFIX) ??
      fallback
    );
  }
  if (error?.response?.status === 409) {
    return getConflictMessage(error.response.data);
  }
  // The BFF is the only source of this status and sends it for exactly one
  // reason, so the body is not read.
  if (error?.response?.status === CSRF_FAILED_STATUS) {
    return i18n.t(SESSION_EXPIRED_KEY);
  }
  if (error?.response?.status === 502) {
    return (
      getCodedMessage(error.response.data, 502, BAD_GATEWAY_CODE_PREFIX) ??
      i18n.t(GENERIC_BAD_GATEWAY_KEY)
    );
  }
  if (error?.response?.status === 503) {
    return (
      getCodedMessage(error.response.data, 503, UNAVAILABLE_CODE_PREFIX) ??
      i18n.t(GENERIC_UNAVAILABLE_KEY)
    );
  }
  return fallback;
}

async function parseBlobBody(blob) {
  let text;
  try {
    text = await blob.text();
  } catch (_) {
    return null;
  }

  const trimmed = typeof text === "string" ? text.trim() : "";
  if (!trimmed) {
    return null;
  }
  try {
    return JSON.parse(trimmed);
  } catch (_) {
    return trimmed;
  }
}

/**
 * Unpack an axios error whose body arrived as a Blob because the request asked
 * for one (`responseType: "blob"`). Returns an error of the same shape with
 * `response.data` replaced by the parsed JSON body, its text when the body is
 * not JSON, or `null` when there is nothing readable. Every other error is
 * handed back untouched, so any error may be passed in.
 *
 * A Blob body hides the status-carrying fields from `getApiErrorMessage`, so
 * it has to be unpacked before the message can be read - never instead of
 * looking at the status.
 */
export async function unpackBlobErrorBody(error) {
  const response = error?.response;
  if (typeof Blob === "undefined" || !(response?.data instanceof Blob)) {
    return error;
  }

  return {
    ...error,
    // `message` is not enumerable on an Error instance, so spreading loses it.
    message: error.message,
    response: { ...response, data: await parseBlobBody(response.data) },
  };
}
